// Removed CellEditRequestEvent import
import { ChangeEvent, FC, MouseEvent, useCallback, useState } from 'react';
import { NumberFormatValues, SourceInfo } from 'react-number-format';
import { useNavigate } from 'react-router-dom';
import { Buttons, Form, FormElements } from '../../../common/AddEditFormLayout/AddEditFormLayout.styles';
import {
  DropdownWrapperLarge,
  DropdownWrapperMedium,
  InputWrapperLarge,
  InputWrapperMedium,
  InputWrapperSmall,
} from '../../../common/FormElementWrappers/FormElementWrappers.styles';
import Header from '../../../common/Header/Header';
import { H2 } from '../../../common/Headings/Headings.styles';
import Button from '../../../components/Button/Button';
import { Variant } from '../../../components/Button/Button.types';
import Dropdown from '../../../components/Dropdown/Dropdown';
import Input from '../../../components/Input/Input';
import MaskedInput from '../../../components/MaskedInput/MaskedInput';
import RadioGroup from '../../../components/RadioGroup/RadioGroup';
import { RadioGroupLayouts } from '../../../components/RadioGroup/RadioGroup.types';
import { EditCellRenderer } from '../../../components/Table/EditCellRenderer/EditCellRenderer';
import Table from '../../../components/Table/Table';
import { STATUS, StatusKeys } from '../../../constants/ApiEnums';
import { OPTIONS_PLACEHOLDER } from '../../../constants/Dropdown';
import {
  RawProdPcaNumberKeys,
  RawProdTypeCodeKeys,
  RawProdUomKeys,
  RawProdUsageAlgoKeys,
  RAW_PROD_PCA_NUMBER,
  RAW_PROD_TYPE_CODE,
  RAW_PROD_TYPE_CODE_LABELS,
  RAW_PROD_UOM,
  RAW_PROD_USAGE_ALGO,
} from '../../../constants/RawProds';
import { useAccounting } from '../../../services/accounting/accounting.service';
import { Accounting } from '../../../services/accounting/accounting.types';
import { useGetClasses } from '../../../services/class/class.service';
import { Class } from '../../../services/class/class.types';
import { useOccasions } from '../../../services/occasion/occasion.service';
import { Occasion } from '../../../services/occasion/occasion.types';
import { SubAssembly } from '../../../services/rawProd/rawProd.types';
import { useStores } from '../../../services/storeControl/storeControl.service';
import { StoreControlResponse } from '../../../services/storeControl/storeControl.types';
import { useGenericErrorMessage } from '../../../utils/useGenericErrorMessage/useGenericErrorMessage';
import { HorizontalLine } from '../../StoreControl/StoreRecord.styles';
import EditSubassembly from '../EditSubassembly/EditSubassembly';
import { RowWrapper, TableRowWrapper } from './RawProdForm.styles';
import { RawProdFlows, RawProdFormProps } from './RawProdForm.types';

const RawProdForm: FC<RawProdFormProps> = ({
  flow,
  headerTitle,
  formValues,
  formErrors,
  setFormErrors,
  setFormValues,
  selectedSubassemblyRawProds,
  setSelectedSubassemblyRawProds,
  handleSave,
  saveDisabled,
  isButtonLoading,
  disabledInputs = [],
}) => {
  const navigate = useNavigate();
  const isNation = formValues.mxRef === 'NATION';
  const isSubassembly = formValues.rawProdTpCode === RAW_PROD_TYPE_CODE.SubAssembly;
  const [editSubassemblyActive, setEditSubassemblyActive] = useState(false);

  const handleEditSubassemblyClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setEditSubassemblyActive(true);
  };

  // fetch classes and map to dropdown options
  const { isError: classesIsError, data: classesData, error: classesError } = useGetClasses();
  const allClasses = classesData?.data || [];
  const classOptions = allClasses.map((item: Class) => {
    return { value: item?.classNo?.toString(), label: `${item?.classNo} - ${item?.description}` };
  });
  // fetch stores and map to dropdown options
  const { isError: storesIsError, data: storesData, error: storesError } = useStores();
  const allStores = storesData?.data || [];
  const storesOptions =
    flow !== RawProdFlows.addLikeNation
      ? [
          ...allStores.map((store: StoreControlResponse) => {
            return { value: store?.storeNo?.toString(), label: store?.storeNo };
          }),
          { value: 'NATION', label: 'NATION - Nation for CCP' },
        ]
      : [
          ...allStores.map((store: StoreControlResponse) => {
            return { value: store?.storeNo?.toString(), label: store?.storeNo };
          }),
        ];
  // fetch occasions and map to dropdown options
  const { isError: occasionsIsError, data: occasionsData, error: occasionsError } = useOccasions(true);
  const allOccasions = occasionsData?.data || [];
  const occasionOptions = allOccasions.map((occasion: Occasion) => {
    return {
      value: occasion?.occasionNo?.toString(),
      label: `${occasion?.occasionNo} - ${occasion?.description}`,
    };
  });
  // fetch accounting codes and map to dropdown options
  const { isError: accountsIsError, data: accountsData, error: accountsError } = useAccounting(true);
  const allAccounts = accountsData?.data || [];
  const oldAccountsOptions = allAccounts
    .filter(
      (account: Accounting) =>
        account?.accountType === 'D' && account?.accountVersion === 'OLD' && account?.accountCode !== '0000000000'
    )
    .map((account: Accounting) => {
      return {
        value: account?.accountCode?.toString(),
        label: `${account?.accountCode} - ${account?.description}`,
      };
    });

  const newAccountsOptions = allAccounts
    .filter(
      (account: Accounting) =>
        account?.accountType === 'D' && account?.accountVersion === 'NEW' && account?.accountCode !== '0000000000'
    )
    .map((account: Accounting) => {
      return {
        value: account?.accountCode?.toString(),
        label: `${account?.accountCode} - ${account?.description}`,
      };
    });

  const handleCellEdit = useCallback(    (event: any) => {
      let newValue = +event.newValue;
      if (newValue > 9999.9999) newValue = 9999.9999;
      if (newValue < -9999.9999) newValue = -9999.9999;
      if (Number.isNaN(newValue)) newValue = event.oldValue;
      setSelectedSubassemblyRawProds((selectedSubassemblyRawProds: SubAssembly[]) =>
        selectedSubassemblyRawProds.map((subassembly: SubAssembly) => {
          if (subassembly.individualMoaRawProdNo === event.data.individualMoaRawProdNo) {
            return {
              ...subassembly,
              usageAmount: newValue,
            };
          } else {
            return subassembly;
          }
        })
      );
    },
    [selectedSubassemblyRawProds]
  );

  const handleSelect = (event: ChangeEvent<HTMLSelectElement>) => {
    setFormValues({ ...formValues, [event.target.id]: event.target.value });
    setFormErrors && setFormErrors({ ...formErrors, [event.target.id]: '' });
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormValues({ ...formValues, [event.target.id]: event.target.value });
    setFormErrors && setFormErrors({ ...formErrors, [event.target.id]: '' });
  };

  const handleMaskedInputChange = (values: NumberFormatValues, sourceInfo: SourceInfo) => {
    if (sourceInfo.event) {
      setFormValues({
        ...formValues,
        [sourceInfo.event.target.id]: values.value === '' ? '' : +values.value,
      });
      setFormErrors && setFormErrors({ ...formErrors, [sourceInfo.event.target.id]: '' });
    }
  };

  const handleChangeBuyLocal = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormValues({ ...formValues, buyLocal: event.target.id === 'buyLocalYes' });
    setFormErrors && setFormErrors({ ...formErrors, buyLocal: '' });
  };

  const handleChangeFrozen = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormValues({ ...formValues, frozen: event.target.id === 'frozenYes' });
    setFormErrors && setFormErrors({ ...formErrors, frozen: '' });
  };

  const handleAddClick = () => {
    navigate(`/tables/rawproducts/add/${formValues.id}`);
  };

  useGenericErrorMessage([
    { error: classesError, isError: classesIsError },
    { error: accountsError, isError: accountsIsError },
    { error: occasionsError, isError: occasionsIsError },
    { error: storesError, isError: storesIsError },
  ]);

  return (
    <>
      <Header breadcrumb={{ label: 'Back to Table', to: '/tables/rawproducts' }} title={headerTitle} />
      {editSubassemblyActive ? (
        <EditSubassembly
          setSelectedSubassemblyRawProds={setSelectedSubassemblyRawProds}
          selectedSubassemblyRawProds={selectedSubassemblyRawProds}
          setEditSubassemblyActive={setEditSubassemblyActive}
          assembledRawProdNo={formValues.moaRawProdNo?.toString() as string}
        />
      ) : (
        <Form>
          <FormElements>
            <H2>Raw Product Table</H2>
            <RowWrapper>
              <DropdownWrapperLarge>
                <Dropdown
                  label='MxRef'
                  id='mxRef'
                  options={storesOptions}
                  value={formValues.mxRef || OPTIONS_PLACEHOLDER}
                  errorText={formErrors?.mxRef}
                  onChange={handleSelect}
                  disabled={disabledInputs.includes('mxRef')}
                />
              </DropdownWrapperLarge>
              <InputWrapperMedium>
                <Input
                  label='MOA Raw Product Number'
                  id='mmsRawProdNo'
                  value={formValues.moaRawProdNo?.toString()}
                  errorText={formErrors?.moaRawProdNo}
                  onChange={handleChange}
                  disabled={disabledInputs.includes('mmsRawProdNo')}
                />
              </InputWrapperMedium>
              <InputWrapperMedium>
                <Input
                  label='Raw Product Number'
                  id='rawProdNo'
                  value={formValues.rawProdNo?.toString()}
                  errorText={formErrors?.rawProdNo}
                  onChange={handleChange}
                  disabled={disabledInputs.includes('rawProdNo')}
                />
              </InputWrapperMedium>
            </RowWrapper>
            <RowWrapper>
              <DropdownWrapperLarge>
                <Dropdown
                  label='Raw Product Type Code'
                  id='rawProdTpCode'
                  options={Object.keys(RAW_PROD_TYPE_CODE_LABELS).map((key: any) => {
                    return { value: key, label: `${key} - ${RAW_PROD_TYPE_CODE_LABELS[key as RawProdTypeCodeKeys]}` };
                  })}
                  value={formValues.rawProdTpCode || OPTIONS_PLACEHOLDER}
                  errorText={formErrors?.rawProdTpCode}
                  onChange={handleSelect}
                  disabled={disabledInputs.includes('rawProdTpCode')}
                />
              </DropdownWrapperLarge>
              <DropdownWrapperMedium>
                <Dropdown
                  label='Status'
                  id='statusNo'
                  options={Object.keys(STATUS).map((key: any) => {
                    return { value: key, label: `${key} - ${STATUS[key as StatusKeys]}` };
                  })}
                  value={formValues?.statusNo?.toString() || OPTIONS_PLACEHOLDER}
                  errorText={formErrors?.statusNo}
                  onChange={handleSelect}
                  disabled={disabledInputs.includes('statusNo')}
                />
              </DropdownWrapperMedium>
              <DropdownWrapperMedium>
                <Dropdown
                  label='Class'
                  id='classNo'
                  options={classOptions}
                  value={formValues?.classNo?.toString() || OPTIONS_PLACEHOLDER}
                  errorText={formErrors?.classNo}
                  onChange={handleSelect}
                  disabled={disabledInputs.includes('classNo')}
                />
              </DropdownWrapperMedium>
              <InputWrapperSmall>
                <MaskedInput
                  label='Print Sequence'
                  id='printSeq'
                  testId='printSeqInput'
                  value={formValues?.printSeq?.toString()}
                  errorText={formErrors?.printSeq}
                  onValueChange={handleMaskedInputChange}
                  isAllowed={(values) => +values.value < 2147483648}
                  disabled={disabledInputs.includes('printSeq')}
                />
              </InputWrapperSmall>
            </RowWrapper>
            {/* Only show table of subassembly data if raw prod type is sub-assembly */}
            {isSubassembly ? (
              <TableRowWrapper>
                <Table
                  headerTitle='Sub-Assembly Raw Products'
                  showHeader={true}
                  onCellEditRequest={handleCellEdit}
                  returnedRecords={selectedSubassemblyRawProds.length}
                  readOnlyEdit={true}
                  columnDefs={[
                    {
                      headerName: 'Raw Product',
                      field: 'individualMoaRawProdNo',
                    },
                    {
                      headerName: 'Description',
                      field: 'description',
                    },
                    {
                      minWidth: 120,
                      flex: 1,
                      field: 'usageAmount',
                      headerName: 'Usage Amt',
                      editable: true,
                      resizable: false,
                      cellRenderer: EditCellRenderer,
                    },
                  ]}
                  rowData={selectedSubassemblyRawProds}
                  // only allow edits to subassembly if mxRef is nation
                  footerButtons={isNation ? [{ label: 'Edit', onClick: handleEditSubassemblyClick }] : undefined}
                />
              </TableRowWrapper>
            ) : (
              <></>
            )}
            <RowWrapper>
              <DropdownWrapperLarge>
                <Dropdown
                  label='PCA Number'
                  id='pcaNo'
                  options={Object.keys(RAW_PROD_PCA_NUMBER).map((key: any) => {
                    return { value: key, label: `${key} - ${RAW_PROD_PCA_NUMBER[key as RawProdPcaNumberKeys]}` };
                  })}
                  value={formValues?.pcaNo?.toString() || OPTIONS_PLACEHOLDER}
                  errorText={formErrors?.pcaNo}
                  onChange={handleSelect}
                  disabled={disabledInputs.includes('pcaNo')}
                />
              </DropdownWrapperLarge>
              <DropdownWrapperLarge>
                <Dropdown
                  label='Occasion'
                  id='occasionNo'
                  options={occasionOptions}
                  value={formValues?.occasionNo?.toString() || OPTIONS_PLACEHOLDER}
                  errorText={formErrors?.occasionNo}
                  onChange={handleSelect}
                  disabled={disabledInputs.includes('occasionNo')}
                />
              </DropdownWrapperLarge>
            </RowWrapper>
            <RowWrapper>
              <DropdownWrapperLarge>
                <Dropdown
                  label='Inventory UOM'
                  id='invenUomNo'
                  options={Object.keys(RAW_PROD_UOM).map((key: any) => {
                    return { value: key, label: `${key} - ${RAW_PROD_UOM[key as RawProdUomKeys]}` };
                  })}
                  value={formValues?.invenUomNo?.toString() || OPTIONS_PLACEHOLDER}
                  errorText={formErrors?.invenUomNo}
                  onChange={handleSelect}
                  disabled={disabledInputs.includes('invenUomNo')}
                />
              </DropdownWrapperLarge>
              <DropdownWrapperLarge>
                <Dropdown
                  label='Prod UOM'
                  id='prodUomNo'
                  options={Object.keys(RAW_PROD_UOM).map((key: any) => {
                    return { value: key, label: `${key} - ${RAW_PROD_UOM[key as RawProdUomKeys]}` };
                  })}
                  value={formValues?.prodUomNo?.toString() || OPTIONS_PLACEHOLDER}
                  errorText={formErrors?.prodUomNo}
                  onChange={handleSelect}
                  disabled={disabledInputs.includes('prodUomNo')}
                />
              </DropdownWrapperLarge>
            </RowWrapper>
            <RowWrapper>
              <InputWrapperSmall>
                <MaskedInput
                  label='Conversion Factor'
                  id='conversionFactor'
                  value={formValues?.conversionFactor?.toString()}
                  errorText={formErrors?.conversionFactor}
                  onValueChange={handleMaskedInputChange}
                  decimalScale={2}
                  isAllowed={(values) => {
                    if (values.floatValue !== undefined && values.floatValue > 9999999.99) return false;
                    return true;
                  }}
                  disabled={disabledInputs.includes('conversionFactor')}
                />
              </InputWrapperSmall>
              <div>
                <RadioGroup
                  options={[
                    {
                      id: 'buyLocalYes',
                      label: 'Yes',
                      disabled: disabledInputs.includes('buyLocal'),
                    },
                    {
                      id: 'buyLocalNo',
                      label: 'No',
                      disabled: disabledInputs.includes('buyLocal'),
                    },
                  ]}
                  id='buyLocal'
                  label='Buy Local'
                  layout={RadioGroupLayouts.Row}
                  selected={
                    formValues?.buyLocal ? 'buyLocalYes' : formValues.buyLocal === false ? 'buyLocalNo' : undefined
                  }
                  onChange={handleChangeBuyLocal}
                  errorText={formErrors?.buyLocal}
                />
              </div>
            </RowWrapper>
            <HorizontalLine />
            <H2>Old Account Code</H2>
            <RowWrapper>
              <DropdownWrapperLarge>
                <Dropdown
                  label='Account Code'
                  id='accountCode'
                  options={oldAccountsOptions}
                  value={formValues?.accountCode || OPTIONS_PLACEHOLDER}
                  errorText={formErrors?.accountCode}
                  onChange={handleSelect}
                  disabled={disabledInputs.includes('accountCode')}
                />
              </DropdownWrapperLarge>
              <InputWrapperLarge>
                <Input
                  label='Short Description'
                  id='shortDescription'
                  testId='shortDescriptionInput'
                  value={formValues?.shortDescription}
                  errorText={formErrors?.shortDescription}
                  onChange={handleChange}
                  disabled={disabledInputs.includes('shortDescription')}
                />
              </InputWrapperLarge>
            </RowWrapper>
            <H2>New Account Code</H2>
            <RowWrapper>
              <DropdownWrapperLarge>
                <Dropdown
                  label='New Account Code'
                  id='newAccountCode'
                  options={newAccountsOptions}
                  value={formValues?.newAccountCode || OPTIONS_PLACEHOLDER}
                  errorText={formErrors?.newAccountCode}
                  onChange={handleSelect}
                  disabled={disabledInputs.includes('newAccountCode')}
                />
              </DropdownWrapperLarge>
              <InputWrapperLarge>
                <Input
                  label='Description'
                  id='description'
                  value={formValues?.description}
                  errorText={formErrors?.description}
                  onChange={handleChange}
                  disabled={disabledInputs.includes('description')}
                />
              </InputWrapperLarge>
            </RowWrapper>
            <HorizontalLine />
            <RowWrapper>
              <DropdownWrapperMedium>
                <Dropdown
                  label='Usage Algorithm'
                  id='usageAlgorithm'
                  options={Object.keys(RAW_PROD_USAGE_ALGO).map((key: any) => {
                    return { value: key, label: RAW_PROD_USAGE_ALGO[key as RawProdUsageAlgoKeys] };
                  })}
                  value={formValues?.usageAlgorithm || OPTIONS_PLACEHOLDER}
                  errorText={formErrors?.usageAlgorithm}
                  onChange={handleSelect}
                  disabled={disabledInputs.includes('usageAlgorithm')}
                />
              </DropdownWrapperMedium>
              <div>
                <RadioGroup
                  options={[
                    {
                      id: 'frozenYes',
                      label: 'Yes',
                      disabled: disabledInputs.includes('frozen'),
                    },
                    {
                      id: 'frozenNo',
                      label: 'No',
                      disabled: disabledInputs.includes('frozen'),
                    },
                  ]}
                  id='frozen'
                  label='Frozen'
                  layout={RadioGroupLayouts.Row}
                  selected={
                    formValues?.frozen === true ? 'frozenYes' : formValues?.frozen === false ? 'frozenNo' : undefined
                  }
                  onChange={handleChangeFrozen}
                  errorText={formErrors?.frozen}
                />
              </div>
              <InputWrapperSmall>
                <MaskedInput
                  label='Safety Policy'
                  id='safetyPolicy'
                  value={formValues?.safetyPolicy?.toString()}
                  errorText={formErrors?.safetyPolicy}
                  onValueChange={handleMaskedInputChange}
                  decimalScale={2}
                  isAllowed={(values) => {
                    if (values.floatValue !== undefined && values.floatValue > 9999999.99) return false;
                    return true;
                  }}
                  disabled={disabledInputs.includes('safetyPolicy')}
                />
              </InputWrapperSmall>
            </RowWrapper>
            {/* only show updated date and updated by for edit */}
            {flow === RawProdFlows.edit ? (
              <RowWrapper>
                <InputWrapperLarge>
                  <Input
                    label='Update By'
                    id='updatedBy'
                    value={formValues.updatedBy}
                    errorText={formErrors?.updatedBy}
                    onChange={handleChange}
                    disabled={disabledInputs.includes('updatedBy')}
                  />
                </InputWrapperLarge>
                <InputWrapperMedium>
                  <Input
                    label='Last Update'
                    id='updatedDate'
                    value={formValues.updatedDate}
                    errorText={formErrors?.updatedDate}
                    onChange={handleChange}
                    disabled={disabledInputs.includes('updatedDate')}
                  />
                </InputWrapperMedium>
              </RowWrapper>
            ) : (
              <></>
            )}
          </FormElements>
          <Buttons>
            <Button label='Save' onClick={handleSave} disabled={saveDisabled} isLoading={isButtonLoading} />
            {/* only show add like nation, add override, and delete buttons if flow is edit & type is not subassembly */}
            {flow === RawProdFlows.edit && !isSubassembly ? (
              <>
                {isNation ? (
                  <Button label='Add Like Nation' variant={Variant.Secondary} onClick={handleAddClick} />
                ) : (
                  <Button label='Add Override' variant={Variant.Secondary} onClick={handleAddClick} />
                )}
                {/* TODO - delete is covered in a future story */}
                {/* <Button label='Delete' variant={Variant.Secondary} disabled /> */}
              </>
            ) : (
              <></>
            )}
          </Buttons>
        </Form>
      )}
    </>
  );
};

export default RawProdForm;
